/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import { withNamespaces } from 'react-i18next';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { Icon } from '../../../ui/Icon';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import { debounce } from '../../../../utils/debounce';
import { defaultData } from '../data';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import {
  containerMarginCss,
  bodyCopyCss,
  richtextCss,
  btnShowMoreLessCss,
  showMoreLessTextCss,
  iconCss,
  showLessCss,
  showAllCss,
  btnLessIconCss,
  headlineCss,
} from './index.styles';

class Content extends SafePureComponent {
  state = {
    showMoreOrLessBtn: false,
    toggleShowMoreBtn: true,
  };

  MIN_RANGE = 200;
  CONTENT_MIN_HEIGHT_SMALL = 390 + this.MIN_RANGE;
  CONTENT_MIN_HEIGHT_MD_AND_UP = 330 + this.MIN_RANGE;

  contentRef = React.createRef();
  innerDescriptionRef = React.createRef();

  updateShowMoreOrLessBtn = () => {
    const { offsetHeight } = this.innerDescriptionRef.current;

    this.setState({
      showMoreOrLessBtn: this.getBreakPointWindow() <= offsetHeight,
    });
  };

  getBreakPointWindow = () => {
    const { innerWidth } = window;
    return innerWidth <= Breakpoints.sm
      ? this.CONTENT_MIN_HEIGHT_SMALL
      : this.CONTENT_MIN_HEIGHT_MD_AND_UP;
  };

  componentDidMount() {
    this.updateShowMoreOrLessBtn();
    window.addEventListener(
      'resize',
      debounce(this.updateShowMoreOrLessBtn, 300),
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'resize',
      debounce(this.updateShowMoreOrLessBtn, 300),
    );
  }

  toggleShowLessContent() {
    this.setState({
      toggleShowMoreBtn: !this.state.toggleShowMoreBtn,
    });
  }

  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const { t } = props;
    const { Description, Headline } = fields;
    const { showMoreOrLessBtn, toggleShowMoreBtn } = this.state;

    return (
      <div css={containerMarginCss}>
        <h2 css={headlineCss}>
          <Text field={Headline} />
        </h2>
        <div
          ref={this.contentRef}
          css={[
            bodyCopyCss,
            showMoreOrLessBtn && showLessCss,
            !toggleShowMoreBtn && showAllCss,
          ]}
        >
          <div ref={this.innerDescriptionRef}>
            <RichText css={richtextCss} field={Description} />
          </div>
        </div>
        {showMoreOrLessBtn && (
          <button
            onClick={() => this.toggleShowLessContent()}
            css={btnShowMoreLessCss}
          >
            <span css={showMoreLessTextCss}>
              {t(toggleShowMoreBtn ? 'SeeMoreCTA' : 'SeeLessCTA')}
            </span>
            <Icon
              name="chevron-down"
              customCss={[iconCss, !toggleShowMoreBtn && btnLessIconCss]}
            />
          </button>
        )}
      </div>
    );
  }
}

Content.displayName = 'LocationHeader/Content';

Content.defaultProps = { fields: defaultData, t: key => key };

const requiredStringPropShape = {
  value: PropTypes.string.isRequired,
};

Content.propTypes = {
  fields: PropTypes.shape({
    Headline: PropTypes.shape(requiredStringPropShape),
    Description: PropTypes.shape(requiredStringPropShape),
  }),
};

export default withNamespaces()(Content);
